package org.teddapp.project.sync

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.createSupabaseClient
import io.github.jan.supabase.auth.Auth
import io.github.jan.supabase.postgrest.Postgrest
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Count
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.query.filter.FilterOperator
import io.github.jan.supabase.realtime.Realtime
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.boolean
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.long
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put

/**
 * Implémentation concrète de [RemoteStore] basée sur Supabase PostgREST.
 *
 * **Hypothèses sur le schéma Supabase :**
 *  - Une table par entité, nommée comme [EntityKind.tableName] (snake_case sauf
 *    `projectStatus` qui reste camelCase pour matcher SQLDelight) — adapter au besoin.
 *  - Colonnes communes obligatoires : `id TEXT PRIMARY KEY`, `updated_at BIGINT`,
 *    `is_deleted BOOL`, `deleted_at BIGINT NULL`.
 *  - Toutes les colonnes du payload existent côté Supabase avec le même nom (snake_case).
 *  - Row Level Security configurée pour que chaque user ne voie que ses lignes.
 *
 * **Limites actuelles :**
 *  - Pas de Realtime intégré ; il faudra wrapper en lecteur dédié.
 *  - Pas d'auth automatique ; il faut un client déjà authentifié.
 *  - Push fait des upserts simples ; pas de retry exponentiel ici (responsabilité
 *    de l'appelant si besoin).
 */
class SupabaseRemoteStore(
    private val client: SupabaseClient,
) : RemoteStore {

    override val pullPageSize: Int get() = PULL_PAGE

    override suspend fun pull(entity: EntityKind, since: Long): List<RemoteRow> {
        // Par numéro d'arrivée, par pages : une page pleine dit qu'il en reste.
        val cursor = if (since > LEGACY_CURSOR_THRESHOLD) 0L else since
        val rows: List<JsonObject> = try {
            client.from(entity.tableName)
                .select {
                    filter { gt("received_seq", cursor) }
                    order(column = "received_seq", order = Order.ASCENDING)
                    limit(PULL_PAGE.toLong())
                }
                .decodeList()
        } catch (e: Exception) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            // Serveur pas encore migré : l'ancienne lecture, par date de modification.
            if (e.message?.contains("received_seq") != true) throw e
            client.from(entity.tableName)
                .select {
                    filter { gt("updated_at", since) }
                    order(column = "updated_at", order = Order.ASCENDING)
                }
                .decodeList()
        }
        return rows.map { json -> toRemoteRow(json) }
    }

    override suspend fun push(entity: EntityKind, rows: List<RemoteRow>) {
        if (rows.isEmpty()) return
        val jsonRows: List<JsonObject> = rows.map { row -> toJsonObject(row) }
        // Upsert sur conflit d'id ; Supabase fait un INSERT ou UPDATE selon la présence.
        client.from(entity.tableName).upsert(jsonRows) {
            // Pas de paramètre onConflict explicite ; l'upsert utilise la PK par défaut.
        }
    }

    /** Compte sans rapatrier : une requête d'en-tête avec le total exact. */
    override suspend fun count(entity: EntityKind): Long? =
        client.from(entity.tableName)
            .select { head = true; count(Count.EXACT) }
            .countOrNull()

    /**
     * Test de connectivité utilisé par le formulaire avant de persister les
     * credentials. On pull `Organization` avec un `since` futur — la réponse
     * est garantie vide mais valide que :
     *   - l'URL est joignable
     *   - la clé anon est acceptée
     *   - la table `organization` existe dans Supabase (schema appliqué)
     *
     * Toute exception (404, 401, DNS, etc.) remonte au caller pour affichage.
     */
    suspend fun ping() {
        client.from(EntityKind.Organization.tableName)
            .select {
                filter { gt("updated_at", Long.MAX_VALUE) }
            }
            .decodeList<JsonObject>()
    }

    // ──────────────────────────────────────────────────────────────────────
    // JSON ↔ RemoteRow
    // ──────────────────────────────────────────────────────────────────────
    private fun toRemoteRow(json: JsonObject): RemoteRow {
        val id = json["id"]?.let { (it as? JsonPrimitive)?.contentOrNull } ?: ""
        val updatedAt = json["updated_at"]?.let { (it as? JsonPrimitive)?.longOrNull } ?: 0L
        // is_deleted peut être stocké en BIGINT (0/1) ou BOOLEAN selon le schéma Postgres ;
        // on accepte les deux formats pour rester robuste.
        val isDeleted = json["is_deleted"]?.let { e ->
            (e as? JsonPrimitive)?.let { p -> p.booleanOrNull ?: (p.longOrNull == 1L) }
        } ?: false
        val deletedAt = json["deleted_at"]?.let { (it as? JsonPrimitive)?.longOrNull }
        val receivedSeq = json["received_seq"]?.let { (it as? JsonPrimitive)?.longOrNull }
        // Payload = tout sauf les colonnes de méta-sync
        val payload = buildMap<String, Any?> {
            for ((k, v) in json) {
                if (k in META_COLUMNS) continue
                put(k, jsonElementToKotlin(v))
            }
        }
        return RemoteRow(
            id = id,
            updatedAt = updatedAt,
            isDeleted = isDeleted,
            deletedAt = deletedAt,
            payload = payload,
            receivedSeq = receivedSeq,
        )
    }

    private fun toJsonObject(row: RemoteRow): JsonObject = buildJsonObject {
        put("id", row.id)
        put("updated_at", row.updatedAt)
        // On envoie is_deleted comme BIGINT (0/1) pour matcher le schéma supabase_schema.sql.
        // Si tu choisis BOOLEAN côté Postgres, remplace par `put("is_deleted", row.isDeleted)`.
        put("is_deleted", if (row.isDeleted) 1L else 0L)
        if (row.deletedAt != null) put("deleted_at", row.deletedAt) else put("deleted_at", JsonNull)
        for ((k, v) in row.payload) {
            if (k in META_COLUMNS) continue
            put(k, kotlinToJsonElement(v))
        }
    }

    private fun jsonElementToKotlin(e: JsonElement): Any? = when (e) {
        is JsonNull -> null
        is JsonPrimitive -> when {
            e.booleanOrNull != null -> e.boolean
            e.longOrNull != null -> e.long
            else -> e.contentOrNull
        }
        else -> e.toString()
    }

    private fun kotlinToJsonElement(v: Any?): JsonElement = when (v) {
        null -> JsonNull
        is Boolean -> JsonPrimitive(v)
        is Long -> JsonPrimitive(v)
        is Int -> JsonPrimitive(v.toLong())
        is Number -> JsonPrimitive(v.toDouble())
        is String -> JsonPrimitive(v)
        else -> JsonPrimitive(v.toString())
    }

    companion object {
        private val META_COLUMNS = setOf("id", "updated_at", "is_deleted", "deleted_at", "synced_at", "received_seq")

        /** Lignes par page de réception : la limite par défaut de PostgREST, sans la dépasser. */
        const val PULL_PAGE: Int = 1000

        /**
         * Helper : crée un SupabaseClient configuré avec Postgrest + Auth + Realtime.
         *
         * Timeout HTTP relevé à 45s : Supabase free tier met le projet en pause
         * après 7 jours d'inactivité, et le premier hit prend 15-30s pour réveiller
         * le projet ("cold start"). Le default Ktor de 10s timeout systématiquement
         * dans ce cas.
         */
        @OptIn(io.github.jan.supabase.annotations.SupabaseInternal::class)
        fun createClient(url: String, anonKey: String): SupabaseClient =
            createSupabaseClient(supabaseUrl = url, supabaseKey = anonKey) {
                install(Postgrest)
                install(Auth)
                install(Realtime)
                httpConfig {
                    install(io.ktor.client.plugins.HttpTimeout) {
                        requestTimeoutMillis = 45_000
                        connectTimeoutMillis = 15_000
                        socketTimeoutMillis = 45_000
                    }
                }
            }
    }
}
